import { useSyncExternalStore } from 'react';

/**
 * Os efeitos de poção ativos do jogador (techspec-pocoes). O servidor manda a lista inteira (`bf`) a cada gole e a cada
 * efeito que acaba; aqui fica a última, para a placa de vida (ícones com o tempo) e para a luz (visão noturna).
 */
export interface BuffAtivo {
  /**
   * forca | destreza | inteligencia | resistir-magia | visao-noturna, e das magias (techspec-magias-de-efeito):
   * forca-menos | destreza-menos | inteligencia-menos | protecao | armadura-reativa | reflexao-magica
   */
  readonly tipo: string;
  /** A poção ou a magia que deu o efeito. Bless e Curse juntam três tipos num ícone só. */
  readonly item: string;
  readonly valor: number;
  /** Fim em ms Unix, no relógio do servidor. */
  readonly fim: number;
  /** O valor no começo (Reflexão e Reativa gastam o `valor`: "4 de 6"). 0/ausente = igual ao valor. */
  readonly total?: number;
}

let atual: readonly BuffAtivo[] = [];
const ouvintes = new Set<() => void>();

export function definirBuffs(lista: readonly BuffAtivo[]): void {
  atual = lista;
  for (const f of ouvintes) f();
}

export function buffsAtuais(): readonly BuffAtivo[] {
  return atual;
}

/** Visão noturna ligada agora: a camada de luz da noite não escurece a tela. */
export function visaoNoturna(agora = Date.now()): boolean {
  return atual.some(b => b.tipo === 'visao-noturna' && b.fim > agora);
}

function assinar(f: () => void): () => void {
  ouvintes.add(f);
  return () => { ouvintes.delete(f); };
}

export function useBuffs(): readonly BuffAtivo[] {
  return useSyncExternalStore(assinar, buffsAtuais, buffsAtuais);
}

/* ── Efeitos dos outros (techspec-magias-de-efeito): o "bo" do servidor, para a placa do alvo ── */

const deOutros = new Map<number, readonly BuffAtivo[]>();
const ouvintesOutros = new Set<() => void>();
let versaoOutros = 0;

export function definirEfeitosDe(id: number, lista: readonly BuffAtivo[]): void {
  if (lista.length === 0) deOutros.delete(id);
  else deOutros.set(id, lista);
  versaoOutros += 1;
  for (const f of ouvintesOutros) f();
}

export function limparEfeitosDeOutros(): void {
  deOutros.clear();
  versaoOutros += 1;
  for (const f of ouvintesOutros) f();
}

function assinarOutros(f: () => void): () => void {
  ouvintesOutros.add(f);
  return () => { ouvintesOutros.delete(f); };
}

const VAZIO: readonly BuffAtivo[] = [];

/** Os efeitos de outro jogador ou de um monstro (o alvo). */
export function useEfeitosDe(id: number | null | undefined): readonly BuffAtivo[] {
  useSyncExternalStore(assinarOutros, () => versaoOutros, () => versaoOutros);
  return id == null ? VAZIO : deOutros.get(id) ?? VAZIO;
}
